import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { CalendarClock } from 'lucide-react'
import moment from 'moment'
import { commitSimulationClockDayOffset } from 'redux/slices/simulationClock'

function formatTimeZoneName(date) {
    return new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
        .formatToParts(date)
        .find((part) => part.type === 'timeZoneName')?.value || ''
}

function formatOffsetLabel(dayOffset) {
    const dayCount = Math.abs(dayOffset)
    const unit = dayCount === 1 ? 'day' : 'days'

    if (dayOffset > 0) {
        return `${dayCount} ${unit} ahead`
    }

    return `${dayCount} ${unit} behind`
}

export default function SimulationClockBanner() {
    const simulationClockDayOffset = useSelector((state) => state.simulationClock.simulationClockDayOffset)

    const [now, setNow] = useState(() => new Date())

    useEffect(() => {
        const intervalId = window.setInterval(() => {
            setNow(new Date())
        }, 1000)

        return () => window.clearInterval(intervalId)
    }, [])

    const clientMoment = moment(now)
    const simulationMoment = moment(now).add(simulationClockDayOffset, 'days')
    const timeZoneName = formatTimeZoneName(simulationMoment.toDate())
    const simulationDateLabel = simulationMoment.format('MMMM D, YYYY')
    const simulationTimeLabel = `${simulationMoment.format('h:mm A')}${timeZoneName ? ` ${timeZoneName}` : ''}`
    const clientDateLabel = clientMoment.format('MMMM D, YYYY')
    const offsetLabel = formatOffsetLabel(simulationClockDayOffset)

    const dayButtonClassName = 'inline-flex h-7 shrink-0 items-center gap-1 rounded-full bg-white px-3 text-sm font-normal text-zinc-800 shadow-sm transition-colors hover:bg-zinc-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#675dff]'
    const resetButtonClassName = 'inline-flex h-7 shrink-0 items-center rounded-full border border-white/60 bg-white/10 px-3 text-sm font-normal text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#675dff]'

    return (
        <div
            className='fixed top-0 left-0 w-full z-50'
            role='region'
            aria-label='Simulation clock'
        >
            <div className='flex h-[44px] w-full shrink-0 items-center gap-x-3 overflow-hidden bg-[#675dff] px-3 text-sm font-normal text-white sm:px-4'>
                <div className='flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-white/15'>
                    <CalendarClock className='h-4 w-4' strokeWidth={2} aria-hidden='true' />
                </div>

                <p className='min-w-0 truncate' title={`${simulationDateLabel} | ${simulationTimeLabel} (${offsetLabel})`}>
                    Current simulation time:
                    {' '}
                    <span className='font-bold'>{simulationDateLabel}</span>
                    {/* {' | '} */}
                    {/* {simulationTimeLabel} */}
                    {' '}
                    <span className='font-bold'>({offsetLabel})</span>
                </p>

                <div className='h-4 w-px shrink-0 bg-white/30' />

                <p className='shrink-0' title={clientDateLabel}>
                    Client date: {clientDateLabel}
                </p>

                <div className='flex-1' />

                <div className='flex shrink-0 items-center gap-x-2'>
                    <button
                        type='button'
                        className={dayButtonClassName}
                        onClick={() => commitSimulationClockDayOffset(simulationClockDayOffset - 7)}
                    >
                        -7 days
                    </button>
                    
                    <button
                        type='button'
                        className={dayButtonClassName}
                        onClick={() => commitSimulationClockDayOffset(simulationClockDayOffset - 1)}
                    >
                        -1 day
                    </button>

                    <button
                        type='button'
                        className={dayButtonClassName}
                        onClick={() => commitSimulationClockDayOffset(simulationClockDayOffset + 1)}
                    >
                        +1 day
                    </button>

                    <button
                        type='button'
                        className={dayButtonClassName}
                        onClick={() => commitSimulationClockDayOffset(simulationClockDayOffset + 7)}
                    >
                        +7 days
                    </button>

                    <button
                        type='button'
                        className={resetButtonClassName}
                        onClick={() => commitSimulationClockDayOffset(0)}
                    >
                        Reset
                    </button>
                </div>
            </div>
        </div>
    )
}
